/**
 * Drogi z etapu — czyste funkcje kreatora (model WF: decyzja = wybór drogi).
 *
 * Jedno miejsce dla logiki, którą współdzielą: lista „Drogi z etapu" w panelu etapu,
 * reducer grafu, zapis (numeracja `sortOrder`), podgląd reguł publikacji i rysowanie krawędzi.
 * Zero Reacta i zero i18n — komponenty podają potrzebne teksty parametrem.
 */

import {
  isApproverConfigComplete,
  isManualRouteEdgeType,
  type MultiApproverMode,
  type StageRuleEdge,
  type StageRuleNode,
} from '@saas/shared';
import type { Edge, Node } from '@xyflow/react';

import type { ApproverConfigValue } from './ApproverPicker';
import type { ProcessEdgeData, ProcessNodeData } from './graphTypes';

type FlowEdge = Edge<ProcessEdgeData>;
type FlowNode = Node<ProcessNodeData>;

/** Znak drogi domyślnej na schemacie (przy nazwie drogi). */
export const DEFAULT_ROUTE_MARK = '★';

/**
 * Czy krawędź jest DROGĄ do wyboru w decyzji — tak samo jak na serwerze (`validateStageRoutes`,
 * `listAvailableRoutes`): zwykła albo warunkowa. Krawędź innego (nieznanego) typu drogą nie jest
 * i nie liczy się do dróg etapu. Brak typu = zwykła (jak w `toStageRuleInput`).
 */
function isRouteEdge(edge: FlowEdge): boolean {
  return isManualRouteEdgeType(edge.data?.edgeType ?? 'unconditional');
}

/**
 * Drogi wychodzące z etapu w kolejności wyświetlania: drogi domyślne zawsze pierwsze (domyślnych
 * warunkowych bywa kilka — między sobą wg `sortOrder`), potem pozostałe wg `sortOrder` rosnąco,
 * remis (albo brak numeru) — wg id krawędzi. W kreatorze procesu tenanta id to id z bazy, więc
 * kolejność jest dokładnie taka, jaką wyliczy serwer (`listAvailableRoutes`); w kreatorze wzoru ITE
 * id krawędzi nadaje klient, więc tam remis rozstrzyga się tylko deterministycznie (bez gwarancji
 * zgodności z kopią wzoru). Zapis numeruje drogi etapu 0..n-1 (`routeSortOrders`), więc remis
 * zdarza się tylko w danych sprzed zapisu. Droga bez numeru ląduje po numerowanych (stan
 * przejściowy w kreatorze). Tylko krawędzie będące drogami (`isRouteEdge`).
 */
export function listStageRoutes(edges: readonly FlowEdge[], sourceId: string): FlowEdge[] {
  return edges
    .map((edge, index) => ({ edge, index }))
    .filter(({ edge }) => edge.source === sourceId && isRouteEdge(edge))
    .sort((a, b) => {
      const aDefault = a.edge.data?.isDefault === true;
      const bDefault = b.edge.data?.isDefault === true;
      if (aDefault !== bDefault) return aDefault ? -1 : 1;
      const aOrder = a.edge.data?.sortOrder;
      const bOrder = b.edge.data?.sortOrder;
      const aNumbered = typeof aOrder === 'number';
      const bNumbered = typeof bOrder === 'number';
      if (aNumbered && bNumbered && aOrder !== bOrder) return aOrder - bOrder;
      // Droga bez numeru ląduje po numerowanych.
      if (aNumbered !== bNumbered) return aNumbered ? -1 : 1;
      // Remis rozstrzyga id krawędzi, jak na serwerze; kolejność w grafie to ostateczność
      // (identyczne id nie powinny się zdarzyć).
      return a.edge.id.localeCompare(b.edge.id) || a.index - b.index;
    })
    .map(({ edge }) => edge);
}

/**
 * Kolejność id dróg etapu po przesunięciu jednej z nich o jedno miejsce. Drogi domyślne zostają
 * przypięte na początku listy: droga domyślna zamienia się miejscami tylko z inną domyślną
 * (od tej kolejności zależy, która z kilku domyślnych dróg wygra w sprawie), a niedomyślna —
 * tylko z niedomyślną; grupy się nie mieszają. Gdy ruch jest niemożliwy, zwraca kolejność bez zmian.
 */
export function moveRoute(
  orderedRoutes: readonly FlowEdge[],
  edgeId: string,
  direction: 'up' | 'down',
): string[] {
  const ids = orderedRoutes.map((e) => e.id);
  const from = orderedRoutes.findIndex((e) => e.id === edgeId);
  const current = orderedRoutes[from];
  if (!current) return ids;
  const to = direction === 'up' ? from - 1 : from + 1;
  const neighbour = orderedRoutes[to];
  if (!neighbour || (neighbour.data?.isDefault === true) !== (current.data?.isDefault === true)) {
    return ids;
  }
  const next = [...ids];
  next[from] = neighbour.id;
  next[to] = edgeId;
  return next;
}

/**
 * `sortOrder` 0..n-1 w obrębie każdego etapu źródłowego (do zapisu): kolejność jak na liście
 * „Drogi z etapu" — domyślna pierwsza, reszta wg dotychczasowego numeru. Dzięki temu numeracja
 * nie ma dziur po usuniętych drogach i nie zależy od kolejności krawędzi w tablicy.
 */
export function routeSortOrders(edges: readonly FlowEdge[]): Map<string, number> {
  const result = new Map<string, number>();
  for (const sourceId of new Set(edges.map((e) => e.source))) {
    listStageRoutes(edges, sourceId).forEach((edge, index) => result.set(edge.id, index));
  }
  return result;
}

/** Klucz i18n domyślnej nazwy węzła danego typu (używany, gdy węzeł nie ma własnej etykiety). */
export function defaultNodeLabelKey(nodeType: string): string {
  const suffix =
    nodeType === 'parallel_split'
      ? 'parallelSplit'
      : nodeType === 'parallel_join'
        ? 'parallelJoin'
        : nodeType;
  return `tenant.processes.builder.nodes.${suffix}DefaultLabel`;
}

/** Nazwa etapu: własna nazwa węzła albo domyślna dla jego typu. */
export function nodeDisplayName(
  node: FlowNode | undefined,
  defaultLabelFor: (nodeType: string) => string,
): string {
  if (!node) return '';
  return node.data?.label?.trim() || defaultLabelFor(node.type ?? '');
}

/**
 * Nazwa, jaką dostanie droga PRZY PUSTEJ nazwie własnej: WŁASNA nazwa etapu docelowego (`targetId`),
 * a gdy i jej nie ma — `unnamedLabel` („Droga bez nazwy", jak w oknie decyzji). Celowo bez domyślnej
 * nazwy typu węzła (`nodeDisplayName`): akceptant takiej nazwy nie zobaczy. To samo zastępstwo
 * stosuje serwer (`listAvailableRoutes`); tu służy też za podpowiedź (placeholder) w polu nazwy drogi.
 */
export function routeFallbackName(
  targetId: string | undefined,
  nodes: readonly FlowNode[],
  unnamedLabel: string,
): string {
  const targetLabel = nodes.find((n) => n.id === targetId)?.data?.label?.trim();
  return targetLabel || unnamedLabel;
}

/**
 * Nazwa drogi na liście i w podglądzie — ta sama kolejność zastępstw co na serwerze
 * (`listAvailableRoutes`): własna nazwa drogi → `routeFallbackName`.
 */
export function routeDisplayName(
  edge: FlowEdge,
  nodes: readonly FlowNode[],
  unnamedLabel: string,
): string {
  const own = edge.data?.label?.trim();
  if (own) return own;
  return routeFallbackName(edge.target, nodes, unnamedLabel);
}

/**
 * Droga bez nazwy własnej wśród ≥ 2 dróg etapu — nazwa jest wtedy wymagana do publikacji
 * (reguła `MANUAL_ROUTE_MISSING_LABEL`), bo akceptant nie odróżniłby dróg po przyciskach.
 * Liczą się wyłącznie krawędzie będące drogami (jak na serwerze).
 */
export function isRouteNameMissing(edge: FlowEdge, stageRoutes: readonly FlowEdge[]): boolean {
  return (
    isRouteEdge(edge) && stageRoutes.filter(isRouteEdge).length >= 2 && !edge.data?.label?.trim()
  );
}

/** Liczba pól wymaganych na drodze (suma po wszystkich kluczach — klasach dokumentu). */
export function requiredFieldsCount(requiredFields: Record<string, string[]> | undefined): number {
  if (!requiredFields) return 0;
  return Object.values(requiredFields).reduce((sum, fields) => sum + fields.length, 0);
}

/**
 * Pola wymagane drogi z serwera / blueprintu: `null` / `{}` / brak = bez bramki → `undefined`
 * w stanie kreatora (przy zapisie wołający zamienia to z powrotem na `null`).
 */
export function normalizeRequiredFields(
  raw: Record<string, string[]> | null | undefined,
): Record<string, string[]> | undefined {
  return raw && Object.keys(raw).length > 0 ? raw : undefined;
}

/**
 * Tryb akceptantów etapu (`any` / `all` / `majority`) z konfiguracji węzła, tak jak liczy go
 * silnik — także ukryty tryb przeniesiony po zmianie typu akceptantów (selektor trybu jest przy
 * typach jednoosobowych niewidoczny, a silnik czyta zapisany tryb). Tryb jest liczony dopiero po
 * wybraniu typu akceptantów (`undefined` = bez typu — reguły zależne od trybu pomijają wtedy
 * etap), brak trybu = `any`. To NIE jest sprawdzenie poprawności konfiguracji — pełną poprawność
 * sprawdza lista wymagań publikacji.
 */
export function stageApprovalMode(
  approverConfig: ApproverConfigValue | undefined,
): MultiApproverMode | undefined {
  return approverConfig?.type ? (approverConfig.mode ?? 'any') : undefined;
}

/**
 * Tryb akceptantów etapu, z którego wychodzi krawędź (`stageApprovalMode` — tryb z konfiguracji,
 * tak jak liczy go silnik); `undefined` dla krawędzi spoza etapu akceptacji (ze startu,
 * z rozdzielenia, ze scalenia), dla braku krawędzi i dla braku węzła źródłowego. Wspólne dla obu
 * kreatorów (proces tenanta i wzór ITE) — zasila blokadę „Droga domyślna" w panelu drogi.
 */
export function approvalModeOfEdgeSource(
  nodes: readonly FlowNode[],
  edge: Pick<FlowEdge, 'source'> | null | undefined,
): MultiApproverMode | undefined {
  if (!edge) return undefined;
  const source = nodes.find((n) => n.id === edge.source);
  return source?.type === 'decision' ? stageApprovalMode(source.data?.approverConfig) : undefined;
}

/**
 * Graf kreatora w kształcie wspólnych reguł publikacji (`validateStageRoutes` z `@saas/shared`) —
 * te same reguły sprawdza serwer, więc podgląd w kreatorze nie może się z nim rozjechać.
 */
export function toStageRuleInput(
  nodes: readonly FlowNode[],
  edges: readonly FlowEdge[],
): { nodes: StageRuleNode[]; edges: StageRuleEdge[] } {
  return {
    nodes: nodes.map((n) => ({
      id: n.id,
      nodeType: n.type ?? '',
      label: n.data?.label,
      // Tryb do reguł tylko przy KOMPLETNEJ konfiguracji akceptantów — tak jak na serwerze, który
      // przy niepoprawnej konfiguracji traktuje etap jak etap bez akceptantów i nie przekazuje
      // trybu (`isApproverConfigComplete` to lustro jego schematu). Blokady przycisków w panelach
      // zostają na trybie surowym (`stageApprovalMode`).
      approvalMode: isApproverConfigComplete(n.data?.approverConfig)
        ? stageApprovalMode(n.data?.approverConfig)
        : undefined,
    })),
    edges: edges.map((e) => ({
      sourceId: e.source,
      targetId: e.target,
      edgeType: e.data?.edgeType ?? 'unconditional',
      label: e.data?.label,
      isDefault: e.data?.isDefault === true,
    })),
  };
}

/**
 * Wygląd krawędzi na schemacie, liczony z jej danych: etykieta = nazwa drogi (droga domyślna
 * z gwiazdką), droga warunkowa rysowana linią przerywaną. Wołane przy renderze płótna — dane
 * grafu (`edge.data`) zostają nietknięte.
 */
export function withRouteAppearance(edge: FlowEdge): FlowEdge {
  const name = edge.data?.label?.trim() ?? '';
  const isDefault = edge.data?.isDefault === true;
  const isConditional = edge.data?.edgeType === 'conditional';
  const label = isDefault ? (name ? `${DEFAULT_ROUTE_MARK} ${name}` : DEFAULT_ROUTE_MARK) : name;
  return {
    ...edge,
    label: label || undefined,
    ...(isConditional ? { style: { ...edge.style, strokeDasharray: '6 4' } } : {}),
  };
}
